import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Loader } from 'semantic-ui-react';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import Image from '@plone/volto/components/theme/Image/Image';
import { searchContent } from '@plone/volto/actions/search/search';
import {
  NEWS_SUBREQUEST,
  EVENTS_SUBREQUEST,
} from '../../config/classicHomeAsyncProps';

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'America/Jamaica',
      })
    : '';

const formatNewsDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const month = d.toLocaleDateString('en-US', {
    month: 'long',
    timeZone: 'America/Jamaica',
  });
  const day = d.toLocaleDateString('en-US', {
    day: 'numeric',
    timeZone: 'America/Jamaica',
  });
  const year = d.toLocaleDateString('en-US', {
    year: 'numeric',
    timeZone: 'America/Jamaica',
  });
  return `${month} ${day}, ${year}`.toUpperCase();
};

const hasNewsImage = (item) =>
  Boolean(item.image_scales?.image?.[0] || item.image_field);

const useSearch = (key) =>
  useSelector((state) => state.search.subrequests[key]);

// Rich news card list with thumbnail, uppercase date, title, and description
const NewsList = ({ result }) => {
  if (!result?.loaded) {
    return (
      <div className="classic-loading-wrap">
        <Loader active inline="centered">
          Loading
        </Loader>
      </div>
    );
  }
  if (!result.items?.length) {
    return <p className="classic-empty">No news yet.</p>;
  }
  return (
    <ul className="classic-news-list">
      {result.items.map((item) => (
        <li key={item['@id']} className="classic-news-item">
          <div className="classic-news-thumb">
            <UniversalLink item={item} aria-hidden="true" tabIndex={-1}>
              {hasNewsImage(item) ? (
                <Image item={item} imageField="image" alt="" loading="lazy" />
              ) : (
                <Image
                  src="/android-chrome-192x192.png"
                  alt=""
                  loading="lazy"
                />
              )}
            </UniversalLink>
          </div>
          <div className="classic-news-body">
            <span className="classic-news-date">
              {formatNewsDate(item.effective)}
            </span>
            <h3 className="classic-news-title">
              <UniversalLink item={item}>{item.title}</UniversalLink>
            </h3>
            {item.description && (
              <p className="classic-news-description">{item.description}</p>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
};

// A short dated list for events, or "Loading" until the search comes back
const DatedList = ({ result, field, empty }) => {
  if (!result?.loaded) {
    return (
      <div className="classic-loading-wrap">
        <Loader active inline="centered">
          Loading
        </Loader>
      </div>
    );
  }
  if (!result.items?.length) return <p className="classic-empty">{empty}</p>;
  return (
    <ul className="classic-list">
      {result.items.map((item) => (
        <li key={item['@id']}>
          <UniversalLink item={item}>{item.title}</UniversalLink>
          <span className="classic-date">{formatDate(item[field])}</span>
        </li>
      ))}
    </ul>
  );
};

// The old site's home page: a title, one paragraph about the Association,
// then News on the left and Resources and Events on the right. Shown instead
// of the redesigned home page when the classic look is on.
const ClassicHome = ({ content }) => {
  const dispatch = useDispatch();
  const news = useSearch(NEWS_SUBREQUEST);
  const events = useSearch(EVENTS_SUBREQUEST);

  useEffect(() => {
    if (!news?.loaded && !news?.loading) {
      dispatch(
        searchContent(
          '',
          {
            portal_type: 'News Item',
            sort_on: 'effective',
            sort_order: 'reverse',
            b_size: 5,
            metadata_fields: [
              'effective',
              'description',
              'image_field',
              'image_scales',
            ],
          },
          NEWS_SUBREQUEST,
        ),
      );
    }
    if (!events?.loaded && !events?.loading) {
      dispatch(
        searchContent(
          '',
          {
            portal_type: 'Event',
            'start.query': new Date().toISOString(),
            'start.range': 'min',
            sort_on: 'start',
            b_size: 5,
            metadata_fields: ['start'],
          },
          EVENTS_SUBREQUEST,
        ),
      );
    }
  }, [dispatch, news?.loaded, news?.loading, events?.loaded, events?.loading]);

  return (
    <Container id="page-document" className="classic-home">
      <h1 className="documentFirstHeading">{content.title}</h1>
      <p className="documentDescription">
        The Aquatic Sports Association of Jamaica (ASAJ) is the governing body
        for aquatics in Jamaica. The Association is affiliated with World
        Aquatics (AQUA) which is the international federation recognised by the
        International Olympic Committee for administering international
        competition in aquatic sports.
      </p>
      <p className="classic-about">
        Learn more in{' '}
        <UniversalLink href="/about">
          the ASAJ&apos;s About Section.
        </UniversalLink>
      </p>
      <div className="classic-columns">
        <section className={`classic-news ${news?.loaded ? '' : 'is-loading'}`}>
          <h2>ASAJ News</h2>
          <NewsList result={news} />
          <p className="classic-older-news">
            <UniversalLink href="/news">View Older News...</UniversalLink>
          </p>
        </section>
        <aside className="classic-side">
          <section className="classic-resources">
            <h2>Resources</h2>
            <p>
              For the rules and regulations, qualification criteria, national
              records, and manuals, alongside World Aquatics&apos;
              constitutions, competition regulations, and various sport-specific
              guides.
            </p>
            <p>
              <UniversalLink href="/resources">
                View the Resources Section.
              </UniversalLink>
            </p>
          </section>
          <div className="classic-divider" aria-hidden="true">
            •
          </div>
          <section
            className={`classic-events ${events?.loaded ? '' : 'is-loading'}`}
          >
            <h2>Events</h2>
            <DatedList
              result={events}
              field="start"
              empty="No upcoming events."
            />
            <div className="classic-browse">
              <span className="classic-browse-label">Browse</span>
              <p>
                <UniversalLink href="/events">Upcoming Events</UniversalLink>
              </p>
              <p>
                <UniversalLink href="/results">Previous Events</UniversalLink>
              </p>
            </div>
          </section>
        </aside>
      </div>
    </Container>
  );
};

export default ClassicHome;
